import React, { CSSProperties, forwardRef, RefForwardingComponent, useEffect } from 'react';

import Card from '@gojob/ui-bits/Card';
import Cta from '@gojob/ui-bits/Cta';
import { useMatchBreakpoint } from '@gojob/ui-bits/hooks/useBreakpoint';
import useLazyRef from '@gojob/ui-bits/hooks/useLazyRef';
import PaletteProvider from '@gojob/ui-bits/styles/PaletteProvider';
import useTheme from '@gojob/ui-bits/styles/useTheme';

import useSortVideoFiles from 'src/components/VideoPromotion/useSortVideoFiles';

export type VideoFile = {
  src?: string;
  type?: string;
};

export type VideoPromotionProps = {
  'aria-label'?: string;
  id?: string;
  href?: string;
  posterSrc?: string;
  mobileFiles?: VideoFile[];
  desktopFiles?: VideoFile[];
  onPlay?: () => void;
  onPosterLoaded?: () => void;
  children?: string;
  className?: string;
  style?: CSSProperties;
};

const useStyles = () => {
  const { spacing, boxShadow } = useTheme();
  return {
    card: {
      height: 'fit-content',
      position: 'relative',
      overflow: 'hidden',
      outline: 'none',
      flexDirection: 'column',
      alignItems: 'stretch',
      transition: 'box-shadow 150ms ease-in-out',
      '&:focus,&:hover': {
        boxShadow: boxShadow('shadow', 5),
      },
    },
    video: {
      width: '100%',
      height: 'auto',
      objectFit: 'cover',
    },
    button: {
      boxSizing: 'border-box',
      position: 'absolute',
      width: `calc(100% - ${spacing(8)})`,
      left: spacing(4),
      right: spacing(4),
      bottom: spacing(4),
    },
  } as const;
};

const renderVideoSource = (file: VideoFile) => {
  const { src, type } = file;

  return <source key={src} data-testid="VideoSource" src={src} type={type} />;
};

const VideoPromotion: RefForwardingComponent<HTMLDivElement, VideoPromotionProps> = (props, ref) => {
  const {
    'aria-label': ariaLabel,
    id,
    href,
    posterSrc,
    onPlay,
    onPosterLoaded,
    className,
    style,
    children,
  } = props;

  /**
   * There's no onPosterLoaded event, so we need to bind our own handler
   */
  const posterImage = useLazyRef(() => {
    const image = new Image();
    return image;
  });

  useEffect(() => {
    if (posterSrc) {
      posterImage.current.onload = () => onPosterLoaded?.();
      posterImage.current.src = posterSrc;

      return () => {
        posterImage.current.onload = null;
      };
    }
  }, [posterSrc, onPosterLoaded]);

  const classes = useStyles();

  const isMobile = useMatchBreakpoint(['xs', 'sm']);

  const desktopFiles = useSortVideoFiles(props.desktopFiles || []);
  const mobileFiles = useSortVideoFiles(props.mobileFiles || []);

  return (
    <PaletteProvider variant="primary">
      <div ref={ref} className={className} style={style}>
        <Card
          as="a"
          id={id}
          aria-label={ariaLabel}
          tabIndex={children ? -1 : 0}
          css={classes.card}
          href={href}
          target="_blank"
        >
          <video
            muted
            autoPlay
            loop
            playsInline
            css={classes.video}
            onPlay={onPlay}
            poster={posterImage.current.src}
          >
            {isMobile ? mobileFiles?.map(renderVideoSource) : desktopFiles?.map(renderVideoSource)}
          </video>

          {children ? (
            <Cta variant="primary" css={classes.button}>
              {children}
            </Cta>
          ) : null}
        </Card>
      </div>
    </PaletteProvider>
  );
};

export default forwardRef(VideoPromotion);
